import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import * as fromPageAlertActions from '@rci/self-serve-fido/shared/page-alert/state/actions';
import { PageContentService } from '@rci/self-serve-fido/shared/page-content/page-content.service';
import { AlertFacadeService, AppAlert, AppAlertType, upsertAlert } from '@rogers/self-serve-common/alert';
import { Observable } from 'rxjs';
import { BannerInfo } from '@rogers/self-serve-common/page-content';

@Injectable()
export class PageAlertsEffects {
  private readonly actions$ = inject(Actions);
  private readonly alertFacadeService = inject(AlertFacadeService);
  private readonly pageContentService = inject(PageContentService);

  loadPageAlerts$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPageAlertActions.PageAlertActionTypes.GetPageAlert),
        withLatestFrom(this.alertFacadeService.getAlerts$('page')),
        filter(([{ page }, appAlerts]) => {
          return !appAlerts.some(alert => alert.uniqueAlertId === `${page}_page_alert`);
        }),
        mergeMap(([{ page }]) => {
          return this.pageContentService.getPageContent$(page).pipe(
            map(({ banners }) => {
              return this.generatePageAlert(page, banners);
            }),
          );
        }),
      ),
    { dispatch: true },
  );

  private generatePageAlert(page: string, banners: BannerInfo[]): Action {
    const pageAlert = banners.find(banner => banner.promoID === 'alert');
    if (!pageAlert) {
      /**
       * Since null can't be returned from an effect that's why I have created an equivalent action for null,
       * that basically does nothing. If null is returned that would throw error.
       */
      return new fromPageAlertActions.NoOpAction();
    }
    const alert: AppAlert = {
      key: `${page}_page_alert`,
      uniqueAlertId: `${page}_page_alert`,
      type: AppAlertType.page,
      alertData: {
        copy: {
          title: pageAlert.copy.heading,
          body: pageAlert.copy.body,
          closeLabel: pageAlert.closeAltText,
        },
        cta: pageAlert.ctas,
        showCloseButton: pageAlert.copy.dismissable === 'true',
        variant: pageAlert.backgroundColor as 'success' | 'error' | 'warning' | 'info',
      },
      iconName: pageAlert.copy.icon?.name ?? '',
    };
    return upsertAlert({ alert });
  }
}
